import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
  hero,
  bioregionalApproach,
  workAreas,
  closingStatement,
  servicesOverview,
  designStudiosHome,
} from "@/content/site";

const IMAGES = {
  hero: {
    src: "/hero-aerial.jpg",
    alt: "Aerial view of a rural village: red-tiled roofs, garden plots, orchards and a greenhouse seen from above.",
  },
};

export default function Home() {
  return (
    <>
      <Seo
        title="Bioregional Ventures | Community Land, Farms & Restoration"
        description="Community land trust consulting, cooperative farming and ecological restoration planning — helping communities secure land, build shared assets and strengthen local food security."
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 md:items-center gap-8 md:gap-0">
          <Reveal className="md:col-span-6 md:col-start-1 md:row-start-1 relative z-10 bg-cream border border-charcoal/10 p-8 md:p-12">
            <p className="text-[min(0.75rem,1.9vw)] md:text-[min(0.75rem,0.88vw)] lg:text-xs uppercase tracking-[0.12em] whitespace-nowrap text-ochre-dark mb-6" data-testid="hero-eyebrow">
              {hero.eyebrow}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight text-charcoal" data-testid="hero-headline">
              {hero.headline}
            </h1>
            {hero.body.map((p) => (
              <p key={p.slice(0, 24)} className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="hero-explore-work-button"
                className="inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
              >
                Discuss a project
              </Link>
              <Link
                to="/approach"
                data-testid="hero-start-conversation-button"
                className="inline-block border border-eucalyptus text-eucalyptus px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
              >
                Explore our approach
              </Link>
            </div>
          </Reveal>
          <div className="md:col-span-7 md:col-start-6 md:row-start-1 overflow-hidden rounded-sm">
            <img
              src={IMAGES.hero.src}
              alt={IMAGES.hero.alt}
              data-testid="hero-image"
              className="w-full h-72 md:h-[560px] object-cover"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      {/* A Bioregional Approach */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <Reveal className="md:col-span-5 overflow-hidden rounded-sm order-2 md:order-1">
            <img
              src={bioregionalApproach.image.src}
              alt={bioregionalApproach.image.alt}
              data-testid="bioregional-approach-image"
              loading="lazy"
              className="w-full h-72 md:h-[440px] object-cover hover:scale-[1.02] transition-transform duration-500"
            />
            <p className="mt-3 text-xs text-charcoal/60 italic">{bioregionalApproach.image.caption}</p>
          </Reveal>
          <Reveal className="md:col-span-6 md:col-start-7 order-1 md:order-2">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="bioregional-approach-heading">
              {bioregionalApproach.heading}
            </h2>
            {bioregionalApproach.body.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
            <div className="mt-8 bg-sage/50 border-l-2 border-ochre p-6" data-testid="bioregion-definition-box">
              <p className="font-serif text-lg md:text-xl leading-relaxed text-charcoal">
                {bioregionalApproach.definition}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Bioregional Design Studios */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-24" data-testid="design-studios-home-section">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="design-studios-home-heading">
            {designStudiosHome.heading}
          </h2>
          <p className="mt-3 font-serif italic text-xl md:text-2xl text-ochre-dark" data-testid="design-studios-home-standfirst">
            {designStudiosHome.standfirst}
          </p>
          <p className="mt-2 text-xs uppercase tracking-[0.15em] text-charcoal/60" data-testid="design-studios-home-attribution">
            {designStudiosHome.attribution}
          </p>
          {designStudiosHome.body.map((p) => (
            <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
              {p}
            </p>
          ))}
          <Link
            to={designStudiosHome.ctaHref}
            data-testid="design-studios-home-cta"
            className="mt-8 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            {designStudiosHome.ctaLabel}
          </Link>
        </Reveal>
      </section>

      {/* Services overview */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-14 md:pt-24 pb-20 md:pb-24">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal max-w-2xl" data-testid="services-overview-heading">
            {servicesOverview.heading}
          </h2>
          {servicesOverview.intro && (
            <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
              {servicesOverview.intro}
            </p>
          )}
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {servicesOverview.cards.map((card, i) => (
            <Reveal key={card.title} delay={i * 0.06} className="h-full">
              <div className="h-full border border-charcoal/15 bg-cream p-8 flex flex-col" data-testid={`service-overview-card-${i + 1}`}>
                <h3 className="font-serif text-xl md:text-2xl text-charcoal leading-snug">{card.title}</h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/85 flex-1">{card.text}</p>
                <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
                  <Link
                    to={card.href}
                    data-testid={`service-overview-link-${i + 1}`}
                    className="self-start text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                  >
                    {card.linkLabel}
                  </Link>
                  {card.href2 && (
                    <Link
                      to={card.href2}
                      data-testid={`service-overview-link-${i + 1}b`}
                      className="self-start text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
                    >
                      {card.linkLabel2}
                    </Link>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 text-center">
          <Link
            to="/contact"
            data-testid="services-overview-cta"
            className="inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            {servicesOverview.ctaLabel}
          </Link>
          <p className="mt-4 text-sm text-charcoal/70">{servicesOverview.ctaSupport}</p>
        </Reveal>
      </section>

      {/* Four areas of work */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-12 md:pb-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">What we do</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal max-w-xl" data-testid="work-areas-heading">
            What we help develop
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-charcoal/90 max-w-2xl">
            Every place has a different starting point. Our work connects local priorities with practical opportunities to secure land, grow food, restore ecosystems and strengthen livelihoods.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {workAreas.map((area, i) => (
            <Reveal key={area.number} delay={i * 0.08} className="h-full">
              <div className="h-full border border-charcoal/15 bg-cream p-8 flex flex-col" data-testid={`work-area-card-${area.number}`}>
                <span className="font-serif text-ochre text-lg">{area.number}</span>
                <h3 className="font-serif text-xl md:text-2xl mt-4 text-charcoal">{area.title}</h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-charcoal/85 flex-1">{area.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10">
          <Link
            to="/our-work"
            data-testid="work-areas-more-link"
            className="text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
          >
            Read more about each area
          </Link>
        </Reveal>
      </section>

      {/* Closing statement */}
      <section className="bg-eucalyptus text-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <Reveal>
            <p className="font-serif text-2xl md:text-4xl leading-snug italic" data-testid="closing-statement">
              {closingStatement}
            </p>
            <Link
              to="/contact"
              data-testid="closing-cta-button"
              className="mt-10 inline-block border border-cream/60 text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-cream hover:text-eucalyptus transition-colors duration-300"
            >
              Start a conversation
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
